import { usePortalValue } from "../../i18n/message.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Connect a trunk" and "Edit" — the trunk dialog (plan-008 ST-UN, design §22.14, R-UI-2), and the
 * card's "Replace" for the password.
 *
 * The fields, in the design's order: a name; host, port and transport; username and password; the
 * numbers, each with "Use as caller ID"; the provider's signalling addresses (optional, needed to
 * receive conversations); how numbers are written, with a live example built from the first number,
 * and how caller ID is presented; the countries the trunk may ring (the reviewed set only);
 * encryption; the codec policy; how the provider sends calls (direct, or to WeTalk's registration —
 * plan-008 ST-KM, UDP only, with the warning that another phone system on the same account competes for
 * incoming calls); then the honest HD sentence and where WeTalk must be allow-listed —
 * WeTalk's SIP host and address rendered from `readSipTrunkSettings`, never a literal.
 *
 * **The password is write-only.** Its field is empty when the dialog opens — on create and on edit —
 * and the dialog is unmounted when it closes, so a typed password lives only as long as the dialog.
 * On edit the field is "Replace password", and left empty it changes nothing.
 *
 * **An inbound-only trunk (plan-015 SIP, ADR 0046)** opens with its outbound half empty and a sentence saying so:
 * left empty it stays receiving only; filled in — the password included — it becomes two-way in one save.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Icon, Input, Radio, Select, Textarea } from "@wetalk/ui";
import {
  CALLER_ID_PRESENTATION,
  SIP_CODEC_POLICY,
  SIP_INBOUND_DELIVERY,
  SIP_MEDIA_ENCRYPTION,
  SIP_TRANSPORT,
  WIRE_NUMBER_FORMAT,
  type SipTransport,
} from "@wetalk/domain";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import { isInboundOnlyTrunkView, type SipTrunkSettingsView, type SipTrunkView } from "../../api/slice/sip-trunk.js";
import {
  CALLER_ID_PRESENTATION_WORD,
  CODEC_POLICY_WORD,
  HD_SENTENCE,
  INBOUND_DELIVERY_CHOICE_WORD,
  INBOUND_DELIVERY_QUESTION,
  INBOUND_ONLY_EDIT_SENTENCE,
  MEDIA_ENCRYPTION_WORD,
  REGISTRATION_NEEDS_UDP,
  REGISTRATION_WARNING,
  TRANSPORT_WORD,
  TRUNK_BILLING_SENTENCE,
  WIRE_NUMBER_FORMAT_WORD,
  countryName,
} from "./sip-trunk-copy.js";
import {
  apiRefusalErrors,
  editsConnection,
  emptyTrunkForm,
  needsNationalCountry,
  nextRowKey,
  outboundLeftEmpty,
  settingChangeOf,
  trunkFormOf,
  verdictOf,
  wireExampleOf,
  type TrunkForm,
  type TrunkFormErrorKey,
  type TrunkFormErrors,
} from "./sip-trunk-form.js";
import { SIP_TRUNK_QUERY_KEY } from "./sip-trunk-query.js";

/** What the dialog tells the page after a save, for the status line under the trunk. */
export interface TrunkSaved {
  readonly mode: "create" | "edit";
  readonly displayName: string;
  /** An edit changed the host, port, transport, username or password: it must be verified again. */
  readonly reverify: boolean;
}

export interface SipTrunkDialogProps {
  /** `null` connects a new trunk; a trunk opens it for editing. */
  readonly trunk: SipTrunkView | null;
  readonly settings: SipTrunkSettingsView | undefined;
  readonly onClose: () => void;
  readonly onSaved: (saved: TrunkSaved) => void;
}

function rowErrorKey(index: number): TrunkFormErrorKey {
  return `number:${index}`;
}

function errorProp(message: string | undefined): { readonly error?: string } {
  return message === undefined ? {} : { error: message };
}

function FieldProblem({ message }: { readonly message: string | undefined }): ReactNode {
  usePortalTranslation("operations");
  if (message === undefined) return null;
  return (
    <span className="nb-field-error" role="alert">
      {message}
    </span>
  );
}

export function SipTrunkDialog({ trunk, settings, onClose, onSaved }: SipTrunkDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const mode = trunk === null ? "create" : "edit";
  const inboundOnly = trunk !== null && isInboundOnlyTrunkView(trunk);
  const reviewed = settings?.destinationCountry.map((one) => one.code) ?? [];
  const [form, setForm] = useState<TrunkForm>(() => (trunk === null ? emptyTrunkForm(reviewed) : trunkFormOf(trunk)));
  const [error, setError] = usePortalValue<TrunkFormErrors>({});

  const patch = (change: Partial<TrunkForm>): void => {
    setForm((held) => ({ ...held, ...change }));
  };

  const save = useMutation({
    mutationFn: async (): Promise<TrunkSaved> => {
      const verdict = verdictOf(form, mode, inboundOnly);
      if (verdict.kind === "invalid") throw new FormInvalid(verdict.error);
      const input = verdict.input;
      if (trunk === null) {
        const made = await gateway.createSipTrunk(input);
        return { mode, displayName: made.displayName, reverify: false };
      }
      const id = trunk.sipTrunkId;
      const change = settingChangeOf(trunk, input);
      // An inbound-only trunk given its outbound half gets it in one update, the password with it (the API's rule).
      const addsOutbound = inboundOnly && !outboundLeftEmpty(form);
      if (addsOutbound) await gateway.updateSipTrunk(id, { ...change, password: form.password });
      else if (Object.keys(change).length > 0) await gateway.updateSipTrunk(id, change);
      const replacing = !inboundOnly && form.password.length > 0;
      if (replacing) await gateway.replaceSipTrunkPassword(id, form.password);
      const kept = new Set(form.number.flatMap((one) => (one.phoneNumberId === null ? [] : [one.phoneNumberId])));
      for (const saved of trunk.number) {
        if (!kept.has(saved.phoneNumberId)) await gateway.removeSipTrunkNumber(id, saved.phoneNumberId);
      }
      for (const row of form.number) {
        if (row.phoneNumberId === null) {
          await gateway.addSipTrunkNumber(id, { e164: row.e164.trim(), isCallerId: row.isCallerId });
          continue;
        }
        const saved = trunk.number.find((one) => one.phoneNumberId === row.phoneNumberId);
        if (saved !== undefined && saved.isCallerId !== row.isCallerId) {
          await gateway.updateSipTrunkNumber(id, row.phoneNumberId, row.isCallerId);
        }
      }
      return { mode, displayName: input.displayName, reverify: editsConnection(change) || replacing || addsOutbound };
    },
    onSuccess: (saved) => {
      onSaved(saved);
      onClose();
    },
    onError: (raised: unknown) => {
      setError(() => (raised instanceof FormInvalid ? raised.error : apiRefusalErrors(raised, portalText("operations", "the_trunk_could_not_be_saved_a2fa5bac"))));
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk }),
        queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunkEvent }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEY.number }),
      ]);
    },
  });

  const setTransport = (transport: SipTransport): void => {
    setForm((held) => ({
      ...held,
      transport,
      // The usual port follows the transport while the member has not typed another one.
      port: transport === "tls" && held.port === "5060" ? "5061" : transport !== "tls" && held.port === "5061" ? "5060" : held.port,
      // "Always — needs TLS" cannot stay chosen without TLS; the closest honest choice remains.
      mediaEncryption: transport !== "tls" && held.mediaEncryption === "srtp_required" ? "srtp_optional" : held.mediaEncryption,
    }));
  };

  const setNumber = (key: string, change: Partial<{ e164: string; isCallerId: boolean }>): void => {
    setForm((held) => ({ ...held, number: held.number.map((one) => (one.key === key ? { ...one, ...change } : one)) }));
  };

  const edge = settings === undefined ? null : `${settings.sipEdgeHost} (${settings.sipEdgeAddress})`;

  return (
    <Dialog
      open
      title={trunk === null ? portalText("operations", "connect_a_trunk_bed9b758") : portalText("operations", "edit_value_ad328111", { v0: String(trunk.displayName) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {portalText("operations", "cancel_77dfd213")}</Button>
          <Button
            variant="primary"
            disabled={save.isPending}
            onClick={() => {
              setError(() => ({}));
              save.mutate();
            }}
          >
            {save.isPending ? portalText("operations", "saving_56a2285c") : trunk === null ? portalText("operations", "connect_the_trunk_615a2894") : portalText("operations", "save_changes_179359b3")}
          </Button>
        </>
      }
    >
      <div className="nb-dialog-scroll">
        <div className="nb-form">
          <p className="c-note">
            {portalText("operations", "the_details_come_from_your_voip_provider_or_phone_system_u_703443c8")}{TRUNK_BILLING_SENTENCE()}
          </p>
          {inboundOnly ? <p className="c-note">{INBOUND_ONLY_EDIT_SENTENCE()}</p> : null}
          {error.form === undefined ? null : (
            <p className="c-note c-note-critical" role="alert">
              {error.form}
            </p>
          )}

          <Field label={portalText("operations", "name_709a2322")} description={portalText("operations", "what_your_team_calls_this_trunk_7a324a9e")} {...errorProp(error.display_name)}>
            {(control) => (
              <Input
                {...control}
                value={form.displayName}
                maxLength={80}
                onChange={(event) => {
                  patch({ displayName: event.currentTarget.value });
                }}
              />
            )}
          </Field>

          <div className="nb-form-row">
            <Field label={portalText("operations", "host_3960ec4c")} description={portalText("operations", "your_provider_s_sip_server_like_sip_provider_com_38638fb9")} {...errorProp(error.host)}>
              {(control) => (
                <Input
                  {...control}
                  font="mono"
                  value={form.host}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => {
                    patch({ host: event.currentTarget.value });
                  }}
                />
              )}
            </Field>
            <Field label={portalText("operations", "port_fe035157")} {...errorProp(error.port)}>
              {(control) => (
                <Input
                  {...control}
                  inputMode="numeric"
                  value={form.port}
                  onChange={(event) => {
                    patch({ port: event.currentTarget.value });
                  }}
                />
              )}
            </Field>
            <Field label={portalText("operations", "transport_c10d76c9")} {...errorProp(error.transport)}>
              {(control) => (
                <Select
                  {...control}
                  value={form.transport}
                  onChange={(event) => {
                    const next = SIP_TRANSPORT.find((one) => one === event.currentTarget.value);
                    if (next !== undefined) setTransport(next);
                  }}
                >
                  {SIP_TRANSPORT.map((one) => (
                    <option key={one} value={one}>
                      {TRANSPORT_WORD[one]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div className="nb-form-row nb-form-row-two">
            <Field label={portalText("operations", "username_84c29015")} {...errorProp(error.auth_username)}>
              {(control) => (
                <Input
                  {...control}
                  value={form.authUsername}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => {
                    patch({ authUsername: event.currentTarget.value });
                  }}
                />
              )}
            </Field>
            <Field
              label={trunk === null || inboundOnly ? portalText("operations", "password_8be3c943") : portalText("operations", "replace_password_3f912c9c")}
              description={
                trunk === null
                  ? portalText("operations", "kept_in_wetalk_s_vault_it_is_never_shown_again_here_or_any_1d5303fb")
                  : inboundOnly
                    ? portalText("operations", "only_with_the_outbound_details_kept_in_wetalk_s_vault_it_i_e76c3212")
                    : portalText("operations", "leave_empty_to_keep_the_current_one_it_is_never_shown_4503ad29")
              }
              {...errorProp(error.password)}
            >
              {(control) => (
                <Input
                  {...control}
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(event) => {
                    patch({ password: event.currentTarget.value });
                  }}
                />
              )}
            </Field>
          </div>

          <fieldset className="nb-fieldset">
            <legend className="nb-legend">{portalText("operations", "numbers_7e53821f")}</legend>
            <p className="nb-hint">
              {portalText("operations", "your_numbers_at_this_provider_in_international_form_with_3_b4534286")}</p>
            <FieldProblem message={error.number} />
            {form.number.map((row, index) => (
              <NumberRow
                key={row.key}
                index={index}
                row={row}
                problem={error[rowErrorKey(index)]}
                onChange={(change) => {
                  setNumber(row.key, change);
                }}
                onRemove={() => {
                  setForm((held) => ({ ...held, number: held.number.filter((one) => one.key !== row.key) }));
                }}
              />
            ))}
            <div>
              <Button
                variant="ghost"
                onClick={() => {
                  setForm((held) => ({
                    ...held,
                    number: [...held.number, { key: nextRowKey(), phoneNumberId: null, e164: "", isCallerId: false }],
                  }));
                }}
              >
                {portalText("operations", "add_a_number_0545db8a")}</Button>
            </div>
          </fieldset>

          <fieldset className="nb-fieldset">
            <legend className="nb-legend">{INBOUND_DELIVERY_QUESTION()}</legend>
            <FieldProblem message={error.inbound_delivery} />
            {SIP_INBOUND_DELIVERY.map((one) => (
              <Radio
                key={one}
                name="inbound-delivery"
                value={one}
                checked={form.inboundDelivery === one}
                disabled={one === "registration" && form.transport !== "udp" && form.inboundDelivery !== "registration"}
                onChange={() => {
                  patch({ inboundDelivery: one });
                }}
                label={INBOUND_DELIVERY_CHOICE_WORD[one]}
              />
            ))}
            {form.transport === "udp" ? null : <p className="nb-hint">{REGISTRATION_NEEDS_UDP()}</p>}
            {form.inboundDelivery === "registration" ? (
              <p className="nb-warning">
                <Icon glyph={GLYPH.warning} size="lg" />
                <span>{REGISTRATION_WARNING()}</span>
              </p>
            ) : null}
          </fieldset>

          <Field
            label={portalText("operations", "allowed_addresses_optional_81d438fd")}
            description={portalText("operations", "your_provider_s_signalling_addresses_needed_to_receive_con_7fd48b91")}
            {...errorProp(error.allowed_address)}
          >
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                spellCheck={false}
                value={form.allowedAddress}
                onChange={(event) => {
                  patch({ allowedAddress: event.currentTarget.value });
                }}
              />
            )}
          </Field>

          <fieldset className="nb-fieldset">
            <legend className="nb-legend">{portalText("operations", "how_your_provider_writes_numbers_2cabeb1b")}</legend>
            <FieldProblem message={error.wire_number_format} />
            {WIRE_NUMBER_FORMAT.map((one) => {
              const example = wireExampleOf(form, one);
              return (
                <Radio
                  key={one}
                  name="wire-number-format"
                  value={one}
                  checked={form.wireNumberFormat === one}
                  onChange={() => {
                    patch({ wireNumberFormat: one });
                  }}
                  label={example === null ? WIRE_NUMBER_FORMAT_WORD[one] : `${WIRE_NUMBER_FORMAT_WORD[one]} — ${example}`}
                />
              );
            })}
            {wireExampleOf(form, "e164_plus") === null ? (
              <p className="nb-hint">{portalText("operations", "add_your_first_number_above_to_see_it_written_each_way_bcb07ebb")}</p>
            ) : null}
          </fieldset>

          <Field label={portalText("operations", "how_caller_id_is_presented_6456271f")} {...errorProp(error.caller_id_presentation)}>
            {(control) => (
              <Select
                {...control}
                value={form.callerIdPresentation}
                onChange={(event) => {
                  const next = CALLER_ID_PRESENTATION.find((one) => one === event.currentTarget.value);
                  if (next !== undefined) patch({ callerIdPresentation: next });
                }}
              >
                {CALLER_ID_PRESENTATION.map((one) => (
                  <option key={one} value={one}>
                    {CALLER_ID_PRESENTATION_WORD[one]}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {needsNationalCountry(form) ? (
            <Field label={portalText("operations", "country_for_the_national_form_c596b789")} {...errorProp(error.national_country)}>
              {(control) => (
                <Select
                  {...control}
                  value={form.nationalCountry}
                  onChange={(event) => {
                    patch({ nationalCountry: event.currentTarget.value });
                  }}
                >
                  <option value="">{portalText("operations", "choose_a_country_7843c8c2")}</option>
                  {(settings?.nationalCountry ?? []).map((code) => (
                    <option key={code} value={code}>
                      {countryName(code)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <fieldset className="nb-fieldset">
            <legend className="nb-legend">{portalText("operations", "countries_this_trunk_may_ring_3fcbe97c")}</legend>
            <p className="nb-hint">
              {portalText("operations", "only_countries_whose_emergency_short_and_premium_rate_numb_c20c171c")}</p>
            <FieldProblem message={error.allowed_destination_country} />
            {settings === undefined ? (
              <p className="nb-hint">{portalText("operations", "reading_the_countries_wetalk_can_ring_c37a2f3b")}</p>
            ) : (
              settings.destinationCountry.map((one) => (
                <label key={one.code} className="nb-check">
                  <input
                    type="checkbox"
                    checked={form.allowedDestinationCountry.includes(one.code)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setForm((held) => ({
                        ...held,
                        allowedDestinationCountry: checked
                          ? [...held.allowedDestinationCountry, one.code]
                          : held.allowedDestinationCountry.filter((code) => code !== one.code),
                      }));
                    }}
                  />
                  <span>
                    {countryName(one.code)}
                    {one.reviewedAt === null ? null : <span className="nb-hint-inline"> {portalText("operations", "reviewed_962b01c2")}{one.reviewedAt}</span>}
                  </span>
                </label>
              ))
            )}
          </fieldset>

          <fieldset className="nb-fieldset">
            <legend className="nb-legend">{portalText("operations", "encryption_0af149c2")}</legend>
            <FieldProblem message={error.media_encryption} />
            {SIP_MEDIA_ENCRYPTION.map((one) => (
              <Radio
                key={one}
                name="media-encryption"
                value={one}
                checked={form.mediaEncryption === one}
                disabled={one === "srtp_required" && form.transport !== "tls"}
                onChange={() => {
                  patch({ mediaEncryption: one });
                }}
                label={MEDIA_ENCRYPTION_WORD[one]}
              />
            ))}
            <p className="nb-hint">
              {portalText("operations", "encrypted_audio_srtp_is_only_as_private_as_the_signalling_b610b37a")}</p>
          </fieldset>

          <fieldset className="nb-fieldset">
            <legend className="nb-legend">{portalText("operations", "voice_quality_966ac5c1")}</legend>
            <FieldProblem message={error.codec_policy} />
            {SIP_CODEC_POLICY.map((one) => (
              <Radio
                key={one}
                name="codec-policy"
                value={one}
                checked={form.codecPolicy === one}
                onChange={() => {
                  patch({ codecPolicy: one });
                }}
                label={CODEC_POLICY_WORD[one]}
              />
            ))}
            <p className="nb-hint">{HD_SENTENCE()}</p>
          </fieldset>

          <p className="c-note">
            {edge === null
              ? portalText("operations", "if_your_provider_allow_lists_addresses_add_wetalk_s_sip_ad_b19bf2c4")
              : portalText("operations", "if_your_provider_allow_lists_addresses_add_value_for_signa_d7dc98f0", { v0: String(edge) })}
          </p>
        </div>
      </div>
    </Dialog>
  );
}

/** A refusal the form found before sending — carried through the mutation to the fields. */
class FormInvalid extends Error {
  override readonly name = "FormInvalid";
  readonly error: TrunkFormErrors;
  constructor(error: TrunkFormErrors) {
    super(portalText("operations", "some_fields_need_attention_68d8f8de"));
    this.error = error;
  }
}

function NumberRow({
  index,
  row,
  problem,
  onChange,
  onRemove,
}: {
  readonly index: number;
  readonly row: TrunkForm["number"][number];
  readonly problem: string | undefined;
  readonly onChange: (change: Partial<{ e164: string; isCallerId: boolean }>) => void;
  readonly onRemove: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const label = portalText("operations", "number_value_a914d68a", { v0: String(String(index + 1)) });
  return (
    <div className="nb-number-row">
      {row.phoneNumberId === null ? (
        <Field label={label} {...errorProp(problem)}>
          {(control) => (
            <Input
              {...control}
              font="mono"
              inputMode="tel"
              value={row.e164}
              placeholder="+302101234567"
              onChange={(event) => {
                onChange({ e164: event.currentTarget.value });
              }}
            />
          )}
        </Field>
      ) : (
        <div className="nb-number-saved">
          <span className="nb-term">{label}</span>
          <span className="nb-def">{row.e164}</span>
          <FieldProblem message={problem} />
        </div>
      )}
      <label className="nb-check">
        <input
          type="checkbox"
          checked={row.isCallerId}
          onChange={(event) => {
            onChange({ isCallerId: event.currentTarget.checked });
          }}
        />
        <span>{portalText("operations", "use_as_caller_id_eb987878")}</span>
      </label>
      <Button variant="ghost" onClick={onRemove} aria-label={portalText("operations", "remove_value_f7ef029d", { v0: String(label) })}>
        {portalText("operations", "remove_e963907d")}</Button>
    </div>
  );
}

export interface ReplacePasswordDialogProps {
  readonly trunk: SipTrunkView;
  readonly onClose: () => void;
  readonly onReplaced: () => void;
}

/** The card's "Replace": a new password, write-only, and nothing ever read back. */
export function ReplacePasswordDialog({ trunk, onClose, onReplaced }: ReplacePasswordDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const replace = useMutation({
    mutationFn: () => gateway.replaceSipTrunkPassword(trunk.sipTrunkId, password),
    onSuccess: () => {
      onReplaced();
      onClose();
    },
    onError: (raised: unknown) => {
      setProblem(() => { const current = apiRefusalErrors(raised, portalText("operations", "the_password_could_not_be_replaced_nothing_has_changed_b4ecb426")); return current.password ?? current.form ?? portalText("operations", "the_password_could_not_be_replaced_nothing_has_changed_b4ecb426"); });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk });
    },
  });
  return (
    <Dialog
      open
      title={portalText("operations", "replace_the_password_of_value_9abe4371", { v0: String(trunk.displayName) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {portalText("operations", "cancel_77dfd213")}</Button>
          <Button
            variant="primary"
            disabled={replace.isPending || password.length === 0}
            onClick={() => {
              setProblem(null);
              replace.mutate();
            }}
          >
            {replace.isPending ? portalText("operations", "replacing_302fcd0f") : portalText("operations", "replace_password_3f912c9c")}
          </Button>
        </>
      }
    >
      <Field
        label={portalText("operations", "new_password_d850ee18")}
        description={portalText("operations", "the_password_your_provider_gave_you_for_this_trunk_it_is_k_9bd8ff33")}
        {...errorProp(problem ?? undefined)}
      >
        {(control) => (
          <Input
            {...control}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => {
              setPassword(event.currentTarget.value);
            }}
          />
        )}
      </Field>
    </Dialog>
  );
}
